import { IArticle, ICreateArticle, IUpdateArticle } from "../models/article.ts";
import { ILazyAttachment } from "../models/zip_content.ts";
import {
  ImportProgressStatus,
  ImportType,
} from "../modules/parts/ImportProgressModal.tsx";
import { processArticle } from "./processArticle.ts";
import { CancelError, CancelManager } from "../lib/cancelManager.ts";
import { zendeskHttpRequest } from "../lib/api/zendeskHttpRequest.ts";

const IMPORT_CONCURRENCY = 3;

export const postArticles = async (
  token: string,
  brandUrl: string,
  articles: ICreateArticle[] | IUpdateArticle[],
  attachments: ILazyAttachment[],
  inlineImages: ILazyAttachment[],
  onNewProgressInfo: (index: number, title: string, type: ImportType) => void,
  onUpdateProgressInfo: (
    index: number,
    status: ImportProgressStatus,
    message: string,
    link: string,
    type?: ImportType,
  ) => void,
) => {
  const cancelManager = CancelManager.getInstance();
  cancelManager.reset();

  try {
    const userData = await zendeskHttpRequest<{ user: { role: string } }>(
      "users/me.json",
      token,
      "GET"
    );

    if (!userData?.user || userData.user.role !== "admin") {
      throw new Error("権限がありません");
    }
  } catch (error) {
    console.error("ユーザー権限のチェックに失敗しました:", error);
    onUpdateProgressInfo(0, "Error", "権限がありません", "");
    return [];
  }

  const results: IArticle[] = new Array(articles.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < articles.length) {
      const idx = nextIndex++;
      const article = articles[idx];
      try {
        cancelManager.checkCancelled();
        const postedArticle = await processArticle(
          token,
          brandUrl,
          article,
          attachments,
          inlineImages,
          (title, type) => onNewProgressInfo(idx, title, type),
          (status, message, link, type) => onUpdateProgressInfo(idx, status, message, link, type),
        );
        results[idx] = postedArticle;
      } catch (error) {
        if (error instanceof CancelError) {
          onUpdateProgressInfo(idx, "Cancel", error.message, "");
          return; // ワーカーを終了
        }
        const errorMessage = error instanceof Error ? error.message : String(error);
        onUpdateProgressInfo(idx, "Error", errorMessage, "");
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  const workers = Array.from(
    { length: Math.min(IMPORT_CONCURRENCY, articles.length) },
    () => worker(),
  );
  await Promise.all(workers);

  return results.filter(Boolean);
};
